import React, { useState, useEffect } from 'react';

function Agent() {
  const [leads, setLeads] = useState([]);
  const [newClientName, setNewClientName] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  // Fetch all leads from the backend on component mount
  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      const response = await fetch('https://onecrmapi.oneaihuman.com/api/calls/leads');
      const data = await response.json();
      setLeads(data);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const handleInitiateCall = async (e) => {
    e.preventDefault();
    setStatusMessage('Initiating call...');
    try {
      const response = await fetch('https://onecrmapi.oneaihuman.com/api/calls/initiate-call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          clientName: newClientName,
          phoneNumber: newPhoneNumber,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setStatusMessage(data.message);
        setNewClientName('');
        setNewPhoneNumber('');
        fetchLeads(); // Refresh the lead list
      } else {
        setStatusMessage(data.message || 'Failed to initiate call.');
      }
    } catch (error) {
      console.error('Error initiating call:', error);
      setStatusMessage('An error occurred. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'qualified':
        return 'bg-green-100 text-green-800';
      case 'not_interested':
        return 'bg-red-100 text-red-800';
      case 'follow_up':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-200 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center p-4 md:p-8 bg-gradient-to-br from-indigo-50 to-purple-100">
      <div className="w-full max-w-5xl">
        <header className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-indigo-900 drop-shadow-md">
            Onehub AI Agent Dashboard
          </h1>
          <p className="mt-2 text-lg text-gray-600 font-medium">Manage and track your AI-driven sales calls.</p>
        </header>

        {/* New Call Form */}
        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl mb-10 transform transition-all duration-300 hover:scale-[1.01]">
          <h2 className="text-2xl font-bold mb-4 text-indigo-800">Initiate a New Call</h2>
          <form onSubmit={handleInitiateCall} className="space-y-4">
            <div>
              <label htmlFor="clientName" className="block text-sm font-semibold text-gray-700">Client Name</label>
              <input
                type="text"
                id="clientName"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                required
              />
            </div>
            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-semibold text-gray-700">Phone Number (e.g., +1234567890)</label>
              <input
                type="tel"
                id="phoneNumber"
                value={newPhoneNumber}
                onChange={(e) => setNewPhoneNumber(e.target.value)}
                className="mt-1 block w-full border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full py-3 px-4 border border-transparent rounded-lg shadow-md text-base font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all transform hover:scale-105"
            >
              Initiate Call
            </button>
          </form>
          {statusMessage && (
            <p className="mt-4 text-center text-sm font-medium text-gray-600">{statusMessage}</p>
          )}
        </div>

        {/* Leads List */}
        <div className="bg-white p-6 md:p-8 rounded-2xl shadow-xl">
          <h2 className="text-2xl font-bold mb-6 text-indigo-800">Leads List</h2>
          <div className="overflow-x-auto rounded-lg shadow-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone Number</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Follow-up Time</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {leads.length > 0 ? (
                  leads.map((lead) => (
                    <tr key={lead._id} className="transition-colors duration-200 hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{lead.clientName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{lead.phoneNumber}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                          {lead.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {lead.followUpTime ? new Date(lead.followUpTime).toLocaleString() : 'N/A'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-6 py-10 text-center text-sm text-gray-500">
                      No leads found. Initiate a new call to see leads here.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Agent;
